import React, {
  type Ref,
  useContext,
  useImperativeHandle,
  useRef,
} from "react";
import { Input } from "reactstrap";
import type { InputProps, InputType } from "reactstrap/lib/Input";
import { FormLabelRenderer } from "./FormLabelRenderer";
import { FormRenderer, type IFormRendererProps } from "./FormRenderer";

export interface InputRef {
  input?: HTMLInputElement;
  onChange: (newValue: string) => void;
}

export const InputRendererContext: React.Context<InputProps> =
  React.createContext({});

export type IInputRendererProps = IFormRendererProps &
  InputProps & {
    inputRef?: Ref<InputRef>;
  };

export const InputRenderer = ({
  input,
  meta,
  label,
  formText,
  tooltip,
  disableErrors,
  inputRef,
  ...rest
}: IInputRendererProps) => {
  const type: InputType = rest.type as InputType;
  const { error, submitting } = meta;

  const contextProps = useContext(InputRendererContext);

  const inputElementRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(
    inputRef,
    () => ({
      get input() {
        return inputElementRef.current;
      },
      onChange: input.onChange,
    }),
    [input.onChange],
  );

  return (
    <FormRenderer
      input={input}
      meta={meta}
      label={label}
      type={type}
      formText={formText}
      tooltip={tooltip}
      disableErrors={disableErrors}
    >
      <FormLabelRenderer label={label}>
        {(placeholderText?: string) => (
          <Input
            {...input}
            {...rest}
            {...contextProps}
            placeholder={placeholderText}
            type={type}
            invalid={!!error && !disableErrors}
            disabled={rest.disabled || submitting}
            innerRef={inputRef ? inputElementRef : undefined}
          />
        )}
      </FormLabelRenderer>
    </FormRenderer>
  );
};

/**
 * @deprecated
 */
export default InputRenderer;
